import React from 'react';
import { Box, Divider, Flex, Text, Tooltip } from '@mantine/core';
import * as Enums from '@/utils/enums';

interface StockBreakdownTooltipProps {
  warehouseStocks: any[];
  totalStockQty: number;
  inventory: any;
  children: React.ReactNode;
}

function StockBreakdownTooltip({ warehouseStocks, totalStockQty, inventory, children }: StockBreakdownTooltipProps) {
  const warehouses = warehouseStocks.filter((s) => s.Warehouse?.WarehouseType === Enums.WarehouseType.Warehouse);
  const vans = warehouseStocks.filter((s) => s.Warehouse?.WarehouseType === Enums.WarehouseType.Mobile);
  const visibleSum = warehouseStocks.reduce((sum, s) => sum + (s.QuantityOnHand ?? 0), 0);
  const otherQty = (inventory?.StockQuantity ?? 0) - visibleSum;
  const hasVisibleStock = warehouses.length > 0 || vans.length > 0;

  const label = (
    <Box style={{ minWidth: 220 }}>
      {warehouses.length > 0 && (
        <>
          <Text size="sm" c="dimmed" mb={8}>Warehouses</Text>
          {warehouses.map((s) => (
            <Flex key={s.ID} justify="space-between" gap="xl" mb={4}>
              <Text size="sm">{s.WarehouseName}</Text>
              <Text size="sm" fw="bold">{s.QuantityOnHand ?? 0}</Text>
            </Flex>
          ))}
        </>
      )}
      {vans.length > 0 && (
        <>
          {warehouses.length > 0 && <Divider my={10} />}
          <Text size="sm" c="dimmed" mb={8}>Vans</Text>
          {vans.map((s) => (
            <Flex key={s.ID} justify="space-between" gap="xl" mb={4}>
              <Text size="sm">{s.WarehouseName}</Text>
              <Text size="sm" fw="bold">{s.QuantityOnHand ?? 0}</Text>
            </Flex>
          ))}
        </>
      )}
      {otherQty > 0 && (
        <>
          {hasVisibleStock && <Divider my={10} />}
          <Flex justify="space-between" gap="xl">
            <Text size="sm" c="dimmed">Other</Text>
            <Text size="sm" fw="bold" c="dimmed">{otherQty}</Text>
          </Flex>
        </>
      )}
      {inventory?.Integrated && (
        <Flex justify="space-between" gap="xl" mt={4}>
          <Text size="sm">Accounting Quantity</Text>
          <Text size="sm" fw="bold">{inventory.Quantity}</Text>
        </Flex>
      )}
      <Divider my={10} />
      <Flex justify="space-between" gap="xl">
        <Text size="sm">Total</Text>
        <Text size="sm" fw="bold">{totalStockQty}</Text>
      </Flex>
    </Box>
  );

  return (
    <Tooltip
      label={label}
      withinPortal
      position="bottom"
      withArrow
      multiline
      events={{ hover: true, focus: false, touch: false }}
      styles={{
        tooltip: {
          backgroundColor: 'white',
          color: 'inherit',
          border: '1px solid #dee2e6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        arrow: { backgroundColor: 'white', border: '1px solid #dee2e6' },
      }}
    >
      {children as React.ReactElement}
    </Tooltip>
  );
}

export default StockBreakdownTooltip;
